import type { TFunction } from 'i18next'
import { mapDraftToForm, type WizardFormValues } from '@/lib/wizard-draft'
import { formatIdrWhole, parseIdrDigits } from '@/lib/utils'

export type DraftSummaryRow = { label: string; value: string }

export type DraftSummarySection = { title: string; rows: DraftSummaryRow[] }

type LabelKey = { key: keyof WizardFormValues; label: string; money?: boolean }

const SECTIONS: Array<{ titleKey: string; fields: LabelKey[] }> = [
  {
    titleKey: 'form.sections.profile',
    fields: [
      { key: 'full_name', label: 'wizard.fullName' },
      { key: 'email', label: 'auth.email' },
      { key: 'birth_place', label: 'wizard.birthPlace' },
      { key: 'birth_date', label: 'wizard.birthDate' },
      { key: 'identity_type', label: 'wizard.identityType' },
      { key: 'identity_number', label: 'wizard.identityNumber' },
      { key: 'npwp_number', label: 'wizard.npwp' },
      { key: 'gender', label: 'wizard.gender' },
      { key: 'marital_status', label: 'wizard.maritalStatus' },
      { key: 'spouse_name', label: 'form.spouseName' },
      { key: 'mother_maiden_name', label: 'wizard.motherName' },
      { key: 'address_street', label: 'wizard.address' },
      { key: 'home_postal_code', label: 'wizard.postalCode' },
      { key: 'mobile_phone', label: 'wizard.mobilePhone' },
      { key: 'home_ownership_status', label: 'wizard.homeOwnership' },
      { key: 'account_purpose', label: 'wizard.accountPurpose' },
    ],
  },
  {
    titleKey: 'form.sections.employment',
    fields: [
      { key: 'occupation', label: 'wizard.occupation' },
      { key: 'company_name', label: 'wizard.companyName' },
      { key: 'industry_field', label: 'wizard.industry' },
      { key: 'position', label: 'wizard.position' },
      { key: 'years_at_company', label: 'wizard.yearsAtCompany' },
      { key: 'annual_income', label: 'wizard.annualIncome' },
      { key: 'office_postal_code', label: 'form.officePostal' },
    ],
  },
  {
    titleKey: 'form.sections.wealth',
    fields: [
      { key: 'njop_value', label: 'form.njop', money: true },
      { key: 'home_location', label: 'form.homeLocation' },
      { key: 'total_savings', label: 'form.totalSavings', money: true },
      { key: 'total_assets', label: 'form.totalAssets', money: true },
      { key: 'other_assets', label: 'form.otherAssets' },
    ],
  },
  {
    titleKey: 'form.sections.bank',
    fields: [
      { key: 'bank_name', label: 'wizard.bankName' },
      { key: 'bank_branch', label: 'wizard.bankBranch' },
      { key: 'account_number', label: 'wizard.accountNumber' },
      { key: 'account_holder', label: 'form.accountHolder' },
      { key: 'bank_account_type', label: 'wizard.accountTypeBank' },
    ],
  },
  {
    titleKey: 'form.sections.emergency',
    fields: [
      { key: 'emergency_name', label: 'wizard.emergencyName' },
      { key: 'emergency_phone', label: 'wizard.emergencyPhone' },
      { key: 'emergency_relation', label: 'wizard.emergencyRelation' },
      { key: 'emergency_postal_code', label: 'wizard.postalCode' },
    ],
  },
  {
    titleKey: 'form.sections.documents',
    fields: [
      { key: 'doc_selfie', label: 'form.docSelfie' },
      { key: 'doc_identity', label: 'form.docIdentity' },
    ],
  },
]

function formatEnum(t: TFunction, field: keyof WizardFormValues, raw: string): string {
  if (field === 'gender') {
    if (raw === 'LAKI_LAKI') return t('form.male')
    if (raw === 'PEREMPUAN') return t('form.female')
  }
  if (field === 'marital_status') {
    const map: Record<string, string> = {
      TIDAK_KAWIN: 'form.single',
      KAWIN: 'form.married',
      JANDA: 'form.widowed',
      DUDA: 'form.divorced',
    }
    const k = map[raw]
    if (k) return t(k)
  }
  if (field === 'identity_type') {
    if (raw === 'KTP') return 'KTP'
    if (raw === 'SIM') return 'SIM'
    if (raw === 'PASSPORT') return t('form.passport', { defaultValue: 'Passport' })
  }
  if (field === 'occupation') {
    const map: Record<string, string> = {
      SWASTA: 'Swasta',
      WIRASWASTA: 'Wiraswasta',
      IBU_RT: 'Ibu RT',
      PROFESIONAL: 'Profesional',
      ASN: 'ASN',
      MAHASISWA: 'Mahasiswa',
      LAINNYA: t('form.other'),
    }
    return map[raw] ?? raw
  }
  if (field === 'home_ownership_status') {
    const map: Record<string, string> = {
      PRIBADI: t('form.owned'),
      KELUARGA: t('form.family'),
      SEWA: t('form.rent'),
      LAINNYA: t('form.other'),
    }
    return map[raw] ?? raw
  }
  if (field === 'account_purpose') {
    const map: Record<string, string> = {
      LINDUNG_NILAI: t('form.hedging'),
      GAIN: t('form.gain'),
      SPEKULASI: t('form.speculation'),
      LAINNYA: t('form.other'),
    }
    return map[raw] ?? raw
  }
  if (field === 'bank_account_type') {
    if (raw === 'TABUNGAN') return 'Tabungan'
    if (raw === 'GIRO') return 'Giro'
    if (raw === 'LAINNYA') return t('form.other')
    return raw
  }
  if (field === 'annual_income') {
    return t(`wizard.annualIncomeOptions.${raw}`, { defaultValue: raw })
  }
  return raw
}

function formatMoneyAmount(raw: unknown): string | null {
  const amount =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim()
        ? parseIdrDigits(raw)
        : 0
  if (!Number.isFinite(amount) || amount <= 0) return null
  const formatted = formatIdrWhole(amount)
  return formatted ? `IDR ${formatted}` : null
}

function displayValue(
  t: TFunction,
  field: LabelKey,
  values: Partial<WizardFormValues>,
): string | null {
  const raw = values[field.key]
  if (raw == null || raw === '') return null
  if (field.money) {
    return formatMoneyAmount(raw)
  }
  if (typeof raw === 'number') {
    if (raw === 0) return null
    return String(raw)
  }
  if (typeof raw === 'boolean') return raw ? t('form.yes') : t('form.no')
  const text = String(raw).trim()
  if (!text) return null
  if (/^\d+$/.test(text) && field.key.endsWith('_value')) {
    return formatMoneyAmount(text)
  }
  return formatEnum(t, field.key, text)
}

export function buildDraftSummarySections(
  t: TFunction,
  draft?: Record<string, unknown>,
): DraftSummarySection[] {
  const values = mapDraftToForm(draft ?? {})
  const out: DraftSummarySection[] = []
  for (const section of SECTIONS) {
    const rows: DraftSummaryRow[] = []
    for (const field of section.fields) {
      const value = displayValue(t, field, values)
      if (value) rows.push({ label: t(field.label), value })
    }
    if (rows.length > 0) out.push({ title: t(section.titleKey), rows })
  }
  return out
}
